import { cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import dataUtils from '@/common/dataUtils';
import documentService from './documentService';
import documentsApi from './documentsApi';
import moduleId from './moduleId';
import moduleIndex from './index.js';
import readService from '@/services/readService';
import favoritesService from '@/services/favoritesService';
import IN_MEMORY_DB from './storeNonReactive';
import storeService from '@/services/storeService';
import { LOCAL_STORAGE_DOCUMENT_SORTER_NAME, LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE } from '@/common/constants';

const DEBUG = false;
const ROOT = { root: true };
const USER_STATE_TYPE = 'documentsUserState';
const TIME_SYNCHRONIZE_DATA = 'documents/synchronizeData.processing';

const state = {
  // --- Server data
  folders: [],
  documentUserStates: [],
  instanceFolderTrees: {},
  simpleFolderTree: {},
  documents: [],
  documentsById: {},
  foldersById: {},
  labels: [],
  labelsMapping: [],
  tags: [],
  tagsMapping: [],

  // --- Derived data
  documentUserStatesByDocId: {},

  // --- UI states
  dataLoaded: false,
  selectedLabels: [],
  selectedTags: [],
  filterCriteria: '',
  fileBrowserOpen: false,
  fileBrowserPath: [],
  fileBrowserPathResolved: '',

  displayedDocumentId: null,
  readAllDialogOpen: false, // read all dialog state true=open, false=close

  sortFiles: false, // true to sort files by importance (mandatoryToRead, quittierung, unread)
  sorterName: localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAME) || 'byImportance', // see SorterMenu
  documentSorterNamesByInstance: JSON.parse(localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE) || '{}'),
  // This is a dummy counter to indicate a change, which is sometimes necessary, for example when recalculating unread count in a nested object
  // where reactivity seems broken
  updateHint: 0,
  unreadCountByInstance: {},
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return state.unreadCountByInstance[instance];
  },
  getDisplayedDocument: (state) => state.documentsById[state.displayedDocumentId],
  getDisplayedDocumentTitle(state) {
    let displayedDocument = state.documentsById[state.displayedDocumentId];
    return displayedDocument ? displayedDocument.title : null;
  },
};

const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    countTreeItems(state);
    state.updateHint++;

    let unreadCountByInstance = {};
    Object.keys(state.instanceFolderTrees).forEach((instance) => {
      let unreadCount = 0;
      unreadCount += state.instanceFolderTrees[instance]._unreadCount;
      unreadCount += state.instanceFolderTrees[instance]._acknowledgeCount;
      unreadCountByInstance[instance] = unreadCount;
    });

    state.unreadCountByInstance = unreadCountByInstance;
  },
  /**
   * simpleFolderTree,
   * instanceFolderTrees,
   * documents,
   * documentsById,
   * foldersById,
   * labels,
   * labelsMapping,
   * tags,
   * tagsMapping,
   * folders,
   * documentUserStates,
   * documentUserStatesByDocId,
   * @param {*} state
   * @param {*} serverData
   */
  setDocumentsData(state, serverData) {
    storeService.saveDataToInMemoryStore(moduleId, state, IN_MEMORY_DB, serverData);
    // utils.logDataOutline('document module', serverData);
    state.folder = serverData.folders;
    state.documentUserStates = serverData.documentUserStates;
    state.instanceFolderTrees = serverData.instanceFolderTrees;
    state.simpleFolderTree = serverData.simpleFolderTree;
    state.documents = serverData.documents;
    state.documentsById = serverData.documentsById;
    state.foldersById = serverData.foldersById;
    state.labels = serverData.labels;
    state.labelsMapping = serverData.labelsMapping;
    state.tags = serverData.tags;
    state.tagsMapping = serverData.tagsMapping;
    state.updateHint++;
    state.dataLoaded = true;
  },

  setFileBrowserPathResolved: (state, path) => (state.fileBrowserPathResolved = path),

  /**
   * Set folder path.
   * @param {array} path An array of folder ids which construct the path down the folder hierarchy
   */
  setFileBrowserPath: (state, path) => (state.fileBrowserPath = path),
  setFileBrowserOpen: (state, { value }) => (state.fileBrowserOpen = value),
  toggleFileBrowser: (state) => (state.fileBrowserOpen = !state.fileBrowserOpen),

  setReadAllDialogOpen: (state, { value }) => {
    // console.debug('setReadAllDialogOpen', value);
    state.readAllDialogOpen = value;
  },
  setSelectedLabels: (state, { value }) => {
    state.selectedLabels = value;
    countTreeItems(state);
  },
  setSelectedTags: (state, { value }) => {
    state.selectedTags = value;
    countTreeItems(state);
  },
  setSortFiles: (state, value) => {
    state.sortFiles = value;
  },
  setSorterName: (state, value) => {
    state.sorterName = value;
    localStorage.setItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAME, value);
  },
  /**
   * Fetch the localStorage value for documentSorterNamesByInstance and reflect it in the store variable with the same name
   * */
  fetchInstanceSorterNames: (state) => {
    state.documentSorterNamesByInstance = JSON.parse(localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE) || '{}');
  },
  setInstanceSorterName: (state, { instance, sorterName }) => {
    console.debug(`setInstanceSorterName instance=${instance} sorterName=${sorterName}`);
    let documentSorterNamesByInstance = JSON.parse(localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE) || '{}');
    documentSorterNamesByInstance[`${instance}`] = sorterName;
    state.documentSorterNamesByInstance = documentSorterNamesByInstance;
    localStorage.setItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE, JSON.stringify(documentSorterNamesByInstance));
  },
  setSorterNameIfNotSet: (state, value) => {
    console.debug('setSorterNameIfNotSet', value);
    if (state.sorterName) return;
    state.sorterName = value;
  },
};

/**
 * Defines the localStorage documentSorterNamesByInstance property. If the user has defined in one module instance a sorter, this is used as the default for all instances.
 * If nothing is defined, the config value from the module configuration is used (property 'defaultSorterName'). The default is 'bySortField' Previously it was 'byImportance'. But changed due to https://innotix.atlassian.net/browse/OP-12168
 * @param rootState - the rootState
 */
function defineInstanceSorterNames(rootState) {
  // if the new document by instance sorter property is not set, then initialize the object using the sorterName and the ui-config
  // set sort definition each time, otherwise, changes in ui-config are only applied for new users or when the app will be reset.
  //if (!localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE)) {
  const documentSorterNamesByInstance = {};
  const storedSorterName = localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAME);
  rootState.configs.moduleConfigsByModuleType.Document.forEach((moduleConfig) => {
    const defaultSorterName = storedSorterName ? storedSorterName : moduleConfig.defaultSorterName ? moduleConfig.defaultSorterName : 'bySortField';
    documentSorterNamesByInstance[`${moduleConfig.instance}`] = defaultSorterName;
  });
  console.debug('documentSorterNamesByInstance', JSON.stringify(documentSorterNamesByInstance, null, 2));
  localStorage.setItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAMES_BY_INSTANCE, JSON.stringify(documentSorterNamesByInstance));
  //}
}

// ------------------------------------------------------
const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  async searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: state.documents,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  // TODO: DELETE as soon as nowhere used
  async updateModuleApplicationCache({ state }) {
    // const { folders, documents, labels, labelsMapping, tags, tagsMapping, documentUserStates } = state;
    // await updateModuleCache(moduleIndex.api, { documents, folders, labels, labelsMapping, tags, tagsMapping, documentUserStates });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.documents, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   *
   * Module Implementation for synchronizing module data
   * Each module MUST IMPLEMENT this action and take responsibility for the following actions:
   * - load module meta data from network (service-worker makes sure these data is always (if online) loaded from network.
   * - if file metadata loaded, these records need to be enhanced with the flag _offline if the file is in application cache
   *   The function getLocalStorageModuleFileState in cachingUtils.js does this together with addCachedState automatically
   * - prepare the received data for storing in vuex store (e.g. create id maps if you need to access the data by id later on)
   * - call a setData mutation to store these data items in module store. Be sure to freeze Objects with Object.freeze(data)
   *   before storing in vuex store if and only if these data items doesn't need to be reactive (performance gain!!!)
   * - After pre-processing of those module data, all documents have to be cached and
   * - after caching, the application cache and the local storage needs to be updated
   */
  async synchronizeData({ commit, rootState, state, rootGetters }) {
    try {
      let instances = [];
      if (rootState.configs && rootState.configs.moduleConfigsByModuleType && rootState.configs.moduleConfigsByModuleType.Document) {
        instances = rootState.configs.moduleConfigsByModuleType.Document.map((d) => d.instance);
        // console.debug('load document module instances', instances);
      }
      const response = await documentsApi.getDocuments(instances);
      console.time(TIME_SYNCHRONIZE_DATA);
      let { folders, documents, labels, labelsMapping, tags, tagsMapping, documentUserStates } = response.data;

      folders.forEach((f) => (f._unreadCount = null)); // NEED to initiate _unreadCount property to make it reactive!
      // build foldersById map with children inside
      const foldersById = dataUtils.buildFoldersByIdAndTree(folders);

      // Generate hierarchy by listing top level folders in instanceFolderTrees (by instance)
      const instanceFolderTrees = dataUtils.buildInstanceFolderTrees(folders);

      const documentUserStatesByDocId = dataUtils.buildIdMap(documentUserStates, 'fileIdFk');

      const simpleFolderTree = documentService.buildMinimizedFolderTrees(folders);
      // Create a map of document id's to document objects aiming for fast access by id: documentsById[13] returns the document with id=13
      let start = Date.now();
      const documentsById = dataUtils.buildIdMap(documents, 'id');
      documents.forEach((document) => {
        // Create a userState object in case it does not exist

        let loadedUserState = documentUserStatesByDocId[document.id];
        if (loadedUserState) {
          document._userState = loadedUserState;
        } else {
          document._userState = {
            proto: true,
            fileIdFk: document.id,
            favorite: 0,
            dateread: null,
            dateconfirmed: null,
            userdatemodified: null,
          };
        }

        document._userState._type = USER_STATE_TYPE;
        favoritesService.assertFavoriteIsSet(document);
        //enhance item with information used for example when searching
        let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({
          moduleType: 'Document',
          instance: document.instance,
        });
        if (document.referenceId) {
          // use params of referenced object. Read state to be checked
          let referencedId = document.referenceId;
          let referenced = documentsById[referencedId];
          //console.debug('found referenced document', document, referenced);

          if (referenced) {
            document = Object.assign(document, {
              originalId: referenced.id,
              filename: referenced.filename,
              checksum: referenced.checksum,
              mandatoryToRead: referenced.mandatoryToRead,
              quittierung: referenced.quittierung,
              _userState: referenced._userState,
            });
            //console.debug('merged', document);
          }
        } else if (!document.filename || !moduleConfig) {
          console.warn('document defines no filename or moduleConfig not found ', document, moduleConfig);
        }
        document._checked = false;
        document._displayLocation = buildFolderPath(foldersById, '', foldersById[document.folderIdFk]);
        document._moduleTitle = moduleConfig ? moduleConfig.title : 'module not defined';
        document._path = `/documents/${document.instance}/${document.id}`;
        document._moduleId = moduleId;
        document.type = document.type ?? 'document';
        dataUtils.addToInstanceFolderTrees(document, instanceFolderTrees, foldersById);
      });

      defineInstanceSorterNames(rootState);
      commit('fetchInstanceSorterNames');

      // apply user states stored in localStorage if the userState is newer than what is stored on the item.
      // Delete from local storage if we have a newer state on the object
      // Do not delete otherwise as it is obvious now, the state has not been stored on backend.
      dataUtils.applyLocalStorageUserState(documentsById, USER_STATE_TYPE, 'fileIdFk');

      let end = Date.now();
      console.debug('Preprocessing of ', documents.length, ' documents required ', end - start, 'ms');

      sortInstanceFolderTrees(instanceFolderTrees);

      let labelsById = dataUtils.buildIdMap(labels, 'id');

      // Associate labels
      dataUtils.associateItems({
        baseItemsById: documentsById,
        associateItemsById: labelsById,
        mappings: labelsMapping,
        baseItemAttribute: 'labels',
        mappingBaseRefAttribute: 'fileIdFk',
        mappedAssociateRefAttribute: 'labelIdFk',
      });
      //dataUtils.associateItemsToBaseList(labelsMapping, documentsById, 'labels', 'fileIdFk');

      // Associate tags
      let tagsById = dataUtils.buildIdMap(tags, 'id');
      dataUtils.associateItems({
        baseItemsById: documentsById,
        associateItemsById: tagsById,
        mappings: tagsMapping,
        baseItemAttribute: 'tags',
        mappingBaseRefAttribute: 'documentIdFk',
        mappedAssociateRefAttribute: 'tagIdFk',
      });

      commit('setDocumentsData', {
        instanceFolderTrees,
        simpleFolderTree,
        documentsById,
        documents,
        foldersById,
        labels,
        labelsMapping,
        tags,
        tagsMapping,
        folders,
        documentUserStates,
        documentUserStatesByDocId,
      });

      console.timeEnd(TIME_SYNCHRONIZE_DATA);
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },

  setDisplayedDocumentId({ state, dispatch, commit }, { documentId }) {
    console.debug('----------- setDisplayedDocumentId', documentId);
    state.displayedDocumentId = documentId;
  },

  /**
   * Marks all unread items within the given folder as "read" and recursively traversing the doc tree.
   * @param {*} state module state
   * @param {*} instance If the instance parameter is defined, the whole module/instance is traversed
   * @param {*} folder If a folder is defined this folder and all below will be marked
   */
  async markAllInAndBelowAsRead({ state, dispatch }, { instance, folder }) {
    console.debug('documents/markAllInAndBelowAsRead', instance, folder);
    if (instance && !folder) {
      let instanceTree = state.instanceFolderTrees[instance];
      if (!instanceTree) {
        return;
      }
      folder = { children: instanceTree.root };
    }

    function addItemsOfGivenLevel(item) {
      let items = [];
      if (item.children) {
        for (let child of item.children) {
          if (child.type === 'folder') {
            let childItems = addItemsOfGivenLevel(child);
            if (childItems) {
              items = items.concat(childItems);
            }
          } else {
            items.push(child);
          }
        }
      }
      return items;
    }

    let allItems = addItemsOfGivenLevel(folder);

    await dispatch(
      'sendbox/markAllAsRead2',
      {
        moduleId,
        items: allItems,
        sendboxTitle: '%count% Dokumente als markiert gelesen',
        snackbarText: '%count% Dokumente wurden als "gelesen" markiert',
      },
      ROOT
    );
    countTreeItems(state);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

// Functions ------------------------------------------------------------------------------------

/**
 * Comparator to sort folder and documents trees.
 * @param {*} a
 * @param {*} b
 */
const folderAndDocumentComparator = (a, b) => {
  let typeA = a.type;
  let typeB = b.type;
  let sortA = a.sort;
  let sortB = b.sort;
  if (typeA === 'folder' && typeB === 'folder') {
    return Number(sortA) - Number(sortB);
  }
  if (typeA === 'folder') {
    // typeB cannot be folder
    return -1; //folder first
  }
  if (typeB === 'folder') {
    return 1;
  }
  if (sortA && sortB) {
    return Number(sortA) - Number(sortB);
  }
  if (sortA && !sortB) return 1;
  if (!sortA && sortB) return -1;
  return 0;
};

/**
 * Sorts all trees within the instanceFolderTrees object
 * @param {*} instanceFolderTrees
 */
function sortInstanceFolderTrees(instanceFolderTrees) {
  Object.keys(instanceFolderTrees).forEach((instanceId) => {
    DEBUG && console.debug('sort root for instance instanceId ', instanceFolderTrees[instanceId]);
    sortRecursive(instanceFolderTrees[instanceId].root);
  });
}

/**
 * Sorts a tree recursively. Each folder can have a 'children' property.
 * @param {*} folders Array of folders
 */
function sortRecursive(folders) {
  folders.sort(folderAndDocumentComparator);
  folders.forEach((folder) => {
    if (folder.children) {
      sortRecursive(folder.children);
    }
  });
}

/**
 * Builds the path in which the folder resides.
 * @param foldersById
 * @param path
 * @param folder
 * @returns {string|*}
 */
function buildFolderPath(foldersById, path, folder) {
  if (folder) {
    let subPath = folder.name + '/' + path;
    if (folder && folder.parentId !== 0) {
      subPath = buildFolderPath(foldersById, subPath, foldersById[folder.parentId]);
    }
    return subPath;
  }
  return path;
}

/**
 * Sum up all values for the given aggregateAttribute (e.g. _unreadCount) in the instanceFolderTrees root array (that is the instances) and stores them on the same level to the aggregateAttribute .
 * @param {*} instanceFolderTrees
 * @param {*} aggregateAttribute
 */
function sumUpAttributeValue(instanceFolderTrees, aggregateAttribute) {
  const reducer = (accumulator, currentValue) => accumulator + (currentValue[aggregateAttribute] !== undefined ? currentValue[aggregateAttribute] : 0);
  Object.keys(instanceFolderTrees).forEach((instance) => {
    let rootItems = instanceFolderTrees[instance].root;
    instanceFolderTrees[instance][aggregateAttribute] = rootItems.reduce(reducer, 0); // + instanceFolderTrees[instance][aggregateAttribute];
  });
}

function isToAcknowledge(doc) {
  return doc.quittierung === 1 && doc._userState && !doc._userState.dateconfirmed;
}

function isRead(doc) {
  return readService.isRead(doc);
}

function hasExpectedLabels(doc, selectedLabels) {
  if (selectedLabels.length === 0) {
    return true;
  }
  if (!doc.labels) {
    return false;
  }
  for (let labelId of selectedLabels) {
    let foundLabel = doc.labels.find((label) => label.id === labelId);
    if (!foundLabel) {
      return false;
    }
  }
  return true;
}

function hasExpectedTags(doc, selectedTags) {
  if (selectedTags.length === 0) {
    return true;
  }
  if (!doc.tags) {
    return false;
  }
  for (let tagId of selectedTags) {
    let foundTag = doc.tags.find((tag) => tag.id === tagId);
    if (!foundTag) {
      return false;
    }
  }
  return true;
}

function countTreeItems(state) {
  let { documents, instanceFolderTrees, foldersById, selectedLabels, selectedTags } = state;
  // let { documents } = IN_MEMORY_DB;
  // -------------------------------------------------------------------
  // count unread items
  console.debug('CountTreeItems');
  dataUtils.countSpecialTreeItems({
    documents,
    instanceFolderTrees,
    foldersById: foldersById,
    attribute: '_unreadCount',
    calculateCountFunc: (doc) => (isRead(doc) ? 0 : isToAcknowledge(doc) ? 0 : 1), // count if unread, but do not count if it is not acknowlegded
  });
  // -------------------------------------------------------------------
  // count items to acknowledge
  dataUtils.countSpecialTreeItems({
    documents,
    instanceFolderTrees,
    foldersById,
    attribute: '_acknowledgeCount',
    calculateCountFunc: (doc) => (isToAcknowledge(doc) ? 1 : 0),
  });

  // TODO: Label and Tags filter implementieren
  // count items which meet the labels filter requirement
  dataUtils.countSpecialTreeItems({
    documents,
    instanceFolderTrees,
    foldersById,
    attribute: '_itemsWithExpectedLabels',
    calculateCountFunc: (doc) => (hasExpectedLabels(doc, selectedLabels) ? 1 : 0),
  });

  // count items which meet the tags filter requirement
  dataUtils.countSpecialTreeItems({
    documents,
    instanceFolderTrees,
    foldersById,
    attribute: '_itemsWithExpectedTags',
    calculateCountFunc: (doc) => (hasExpectedTags(doc, selectedTags) ? 1 : 0),
  });
}
